import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { useUserSession } from "../../hooks/useUserSession";
import { useEnabledApplications } from "../../hooks/useEnabledApplications";
import { type IPage } from "../../interfaces/page";
import logging from "../../config/logging";
import AppCardPage from "../AppCardPage/AppCardPage";
import PreLogin from "../PreLogin/PreLogin";
import { LoadingPage } from "../../components/LoadingPage/LoadingPage";
import { UserAccountsService, UserGroup } from "../../gen/clients/llts";
import { ErrorPage } from "../../components/ErrorPage/ErrorPage";
import { SUPPORTED_APPS } from "../../utils/constants";
import { ClientSelector } from "./SubPages/ClientSelector";
import { useNavigation } from "../../hooks/useNavigation";
import { setInitialPagePath } from "../../store/userSession/userSessionActions";

const CLIENT_ROLES = [UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE];

const HomePage: FC<IPage> = props => {
  const { t } = useTranslation();
  const { history } = useNavigation();
  const dispatch = useDispatch();
  const { id } = props;
  const name = t(`${id}.title`);

  const { username, email, isFederatedSsoAccount, roles = [], initialPagePath } = useUserSession();

  const {
    data: enabledApplications,
    isLoading: isEnabledApplicationsLoading,
    isFetching: isEnabledApplicationsFetching,
    error: enabledApplicationsError,
    refetch: refetchEnabledApplications
  } = useEnabledApplications();

  const {
    data: userAccount,
    isLoading: isUserAccountLoading,
    isFetching: isUserAccountFetching,
    error: userAccountError,
    refetch: refetchUserAccount
  } = useQuery(["getUserAccount", username], {
    enabled: !!username,
    queryFn: UserAccountsService.getUserAccount,
    // The onSuccess callback is needed to refresh the enabled applications when the user account is updated.
    onSuccess: () => refetchEnabledApplications()
  });

  useEffect(() => {
    logging.info(`Loading ${name}`);
  }, [name]);

  useEffect(() => {
    sessionStorage.removeItem("accordionData");
  }, []);

  useEffect(() => {
    if (initialPagePath) {
      history.push(initialPagePath);
      dispatch(setInitialPagePath(""));
    }
  }, [history, initialPagePath, dispatch]);

  const cardItems = React.useMemo(() => {
    let theNames: string[] = [];
    if (enabledApplications && enabledApplications.length) {
      theNames = SUPPORTED_APPS.filter(appName => enabledApplications.includes(appName));
    }
    return theNames;
  }, [enabledApplications]);

  if (isUserAccountLoading || isUserAccountFetching || isEnabledApplicationsLoading || isEnabledApplicationsFetching) {
    return <LoadingPage />;
  }

  if (userAccountError) {
    return <ErrorPage apiError={userAccountError} />;
  }

  if (enabledApplicationsError) {
    return <ErrorPage apiError={enabledApplicationsError} />;
  }

  // For client/client contacts, if clientConfig is not set, display client selector.
  if (
    userAccount &&
    email &&
    !userAccount.clientConfig &&
    CLIENT_ROLES.filter(value => roles.includes(value)).length > 0
  ) {
    return <ClientSelector email={email} onSuccess={refetchUserAccount} />;
  }

  return (
    <>
      {email ? (
        <AppCardPage appCardNames={cardItems || []} name={isFederatedSsoAccount ? email : username} />
      ) : (
        <PreLogin />
      )}
    </>
  );
};

export default HomePage;
